import { useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import type { QuizrunGroup, UpcomingEvent } from "@/api/eventDiscovery";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RegistrationErrorBoundary } from "@/components/profile/RegistrationErrorBoundary";
import { RegisteredCard, RegisterRow } from "./cards";

interface QuizrunSectionProps {
  group: QuizrunGroup;
  defaultOpen: boolean;
  personId: number;
  activeEventId: number | null;
  onToggleEvent: (event: UpcomingEvent) => void;
  onChanged: () => void;
  onCloseEvent: () => void;
  /** Single-event register page: render flat/always-open, no collapsible header. */
  flat?: boolean;
}

export function QuizrunSection({
  group,
  defaultOpen,
  personId,
  activeEventId,
  onToggleEvent,
  onChanged,
  onCloseEvent,
  flat = false,
}: QuizrunSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  // Force the group open when it holds the active inline panel (covers the
  // ?event_id= deep-link landing on an otherwise-collapsed group).
  const events = [...group.cities, ...group.virtual];
  const hasActive =
    activeEventId !== null && events.some((e) => e.event_id === activeEventId);
  useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  const body =
    group.is_registered && group.registered_event ? (
      <RegistrationErrorBoundary>
        <RegisteredCard
          event={group.registered_event}
          personId={personId}
          active={activeEventId === group.registered_event.event_id}
          onManage={onToggleEvent}
          onChanged={onChanged}
          onClose={onCloseEvent}
        />
      </RegistrationErrorBoundary>
    ) : (
      <ul className="space-y-2">
        {/* While a city's inline register panel is open, collapse the group to
            just that city — the others reappear on Cancel. */}
        {(hasActive
          ? events.filter((e) => e.event_id === activeEventId)
          : events
        ).map((e) => (
          <RegisterRow
            key={e.event_id}
            event={e}
            active={activeEventId === e.event_id}
            personId={personId}
            onRegister={onToggleEvent}
            onChanged={onChanged}
            onClose={onCloseEvent}
          />
        ))}
      </ul>
    );

  // Single-event register page: no collapsible header — just the event, flat.
  if (flat) {
    return <div className="space-y-3">{body}</div>;
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="mt-8 pt-2 first:mt-0 first:pt-0"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-2 text-left hover:text-primary"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {group.quizrun_title}
          </span>
          {group.is_registered && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Registered
            </span>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-1">{body}</CollapsibleContent>
    </Collapsible>
  );
}
